import { ReferenceTypeOptions } from './../../../utils/enum-maps';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal, ViewEncapsulation, WritableSignal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import {
  ReferenceType,
  TlrTypeEnum,
  TaScopeEnum,
  EquipmentProcessServiceEnum,
  CreateRequest,
  TlrEntryDto,
  UpdateRequest
} from '@fdm/api-client';
import { convertEnumValues, utcToLocal } from '@fdm/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs';

export interface ReferenceDialogData {
  type: 'reference' | 'tlr';
  generalTabId: string;
  editData?: any;
  existingData?: any[];
}

@Component({
  selector: 'app-reference-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatDatepickerModule,
    MatIconModule,
    MatProgressSpinnerModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './reference-dialog.component.html',
  styleUrl: './reference-dialog.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ReferenceDialogComponent implements OnInit {
  private _dialogRef = inject(MatDialogRef<ReferenceDialogComponent>);
  private _data = inject<ReferenceDialogData>(MAT_DIALOG_DATA);
  private _fb = inject(FormBuilder);
  private _destroyRef = inject(DestroyRef);

  form!: FormGroup;
  dialogType: 'reference' | 'tlr' = 'reference';
  isEditMode = false;
  loading: WritableSignal<boolean> = signal(false);
  onSubmit: ((data: any, isEdit: boolean) => void) | null = null;
  queryControl = new FormControl<string | null>(null);

  // Enums for dropdowns
  referenceTypes = ReferenceTypeOptions;
  tlrTypes: [string, string][] = [];
  taScopes: [string, string][] = [];
  equipmentProcessServices: [string, string][] = [];

  ngOnInit(): void {
    this.dialogType = this._data.type;
    this.isEditMode = !!this._data.editData;

    this.tlrTypes = Object.entries(convertEnumValues(TlrTypeEnum));
    this.taScopes = Object.entries(convertEnumValues(TaScopeEnum));
    this.equipmentProcessServices = Object.entries(convertEnumValues(EquipmentProcessServiceEnum));
    this.initForm();
    this._initTriggers();

    if (this.isEditMode) {
      if (this.dialogType === 'reference') {
        this.form.get('type')?.disable({ emitEvent: false });
        this.form.get('number')?.disable({ emitEvent: false });
        this.form.get('description')?.disable({ emitEvent: false });
        this.form.get('status')?.disable({ emitEvent: false });
        this.form.get('link')?.disable({ emitEvent: false });
      } else {
        this.form.get('tag')?.disable({ emitEvent: false });
        // this.form.get('tlrType')?.disable({ emitEvent: false });
      }
    }
  }

  private initForm(): void {
    if (this.dialogType === 'reference') {
      this.form = this._fb.group({
        id: [this._data.editData?.id || null],
        type: [this._data.editData?.type || null, Validators.required],
        number: [this._data.editData?.number || '', Validators.required],
        description: [this._data.editData?.description || ''],
        status: [this._data.editData?.status || ''],
        link: [this._data.editData?.link || ''],
      });
    } else {
      this.form = this._fb.group({
        id: [this._data.editData?.id || null],
        tag: [this._data.editData?.tag || '', Validators.required],
        tlrType: [this._data.editData?.tlrType || null, Validators.required],
        taScope: [this._data.editData?.taScope || null, Validators.required],
        equipmentProcessService: [this._data.editData?.equipmentProcessService || null, Validators.required],
        dateTlrInstalled: [this._data.editData?.dateTlrInstalled ? new Date(this._data.editData.dateTlrInstalled) : null, Validators.required],
        recommendedPermanentRepairDate: [this._data.editData?.recommendedPermanentRepairDate ? new Date(this._data.editData.recommendedPermanentRepairDate) : null, Validators.required],
      });
    }
  }

  private _initTriggers() {
    this.queryControl.valueChanges
      .pipe(
        debounceTime(500),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(query => {

        const options = Object.entries(convertEnumValues(EquipmentProcessServiceEnum)) as [string, string][];

        this.equipmentProcessServices = query 
          ? options.filter(option => option?.[0].toLowerCase().includes(query.toLowerCase()))
          : options;

      });
  }

  onAdd(): void {
    if (this.form.valid && this.onSubmit) {
      const formValue = this.form.getRawValue();

      if (this.dialogType === 'reference') {
        if (this.isEditMode) {
          // For edit mode, pass the form value with id separately
          this.onSubmit(formValue, this.isEditMode);
        } else {
          const request = new CreateRequest({
            type: formValue.type,
            number: formValue.number,
          });
          this.onSubmit(request, this.isEditMode);
        }
      } else {
        const request = new TlrEntryDto({
          generalTabId: this._data.generalTabId,
          tag: formValue.tag,
          tlrType: formValue.tlrType,
          taScope: formValue.taScope,
          equipmentProcessService: formValue.equipmentProcessService,
          dateTlrInstalled: utcToLocal(formValue.dateTlrInstalled)?.toDate(),
          recommendedPermanentRepairDate: utcToLocal(formValue.recommendedPermanentRepairDate)?.toDate(),
        });

        this.onSubmit(request, this.isEditMode);
      }
    }
  }

  onClose(): void {
    if (!this.loading()) {
      this._dialogRef.close();
    }
  }
  getCancelPermission(): boolean {
    if (this.dialogType === 'reference' && this.isEditMode) {
      return true;
    } else {
       return false;

    }
  }

  getTitle(): string {
    if (this.dialogType === 'reference') {
      return this.isEditMode ? 'Edit Reference' : 'Add Reference';
    }
    return this.isEditMode ? 'Edit TLR Reference' : 'Add TLR Reference';
  }
}
